import { useState, useEffect } from "react";

// Hours per weekday (0=Sun ... 6=Sat) in minutes from midnight.
// `close` may exceed 1440 to represent after-midnight closing (e.g., 2 AM = 1560).
const HOURS_MAP = {
  0: { open: 17 * 60, close: 24 * 60 },      // Sun 5 PM – 12 AM
  1: null,                                    // Mon closed
  2: null,                                    // Tue closed
  3: { open: 17 * 60, close: 26 * 60 },      // Wed 5 PM – 2 AM
  4: { open: 17 * 60, close: 26 * 60 },      // Thu 5 PM – 2 AM
  5: { open: 17 * 60, close: 26 * 60 },      // Fri 5 PM – 2 AM
  6: { open: 17 * 60, close: 26 * 60 },      // Sat 5 PM – 2 AM
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function fmt(mins) {
  const m = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return mm === 0 ? `${h12} ${ampm}` : `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

export function getOpenStatus(date = new Date()) {
  // Convert to America/New_York (Atlanta) local fields
  const et = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const dow = et.getDay();
  const nowMin = et.getHours() * 60 + et.getMinutes();

  // Current day's session (portion before midnight)
  const today = HOURS_MAP[dow];
  if (today) {
    const endBeforeMidnight = today.close > 1440 ? 1440 : today.close;
    if (nowMin >= today.open && nowMin < endBeforeMidnight) {
      return { isOpen: true, label: `Open till ${fmt(today.close)}`, closesAt: fmt(today.close) };
    }
  }

  // Spillover from previous day's after-midnight session
  const prevDow = (dow + 6) % 7;
  const prev = HOURS_MAP[prevDow];
  if (prev && prev.close > 1440) {
    const spilloverEnd = prev.close - 1440;
    if (nowMin < spilloverEnd) {
      return { isOpen: true, label: `Open till ${fmt(prev.close)}`, closesAt: fmt(prev.close) };
    }
  }

  // Closed — find next opening
  for (let i = 0; i < 8; i++) {
    const d = (dow + i) % 7;
    const h = HOURS_MAP[d];
    if (h) {
      if (i === 0 && nowMin < h.open) {
        return { isOpen: false, closesAt: null, reopenDay: "Today", reopenTime: fmt(h.open) };
      }
      if (i > 0) {
        const reopenDay = i === 1 ? "Tomorrow" : DAY_NAMES[d];
        return { isOpen: false, closesAt: null, reopenDay, reopenTime: fmt(h.open) };
      }
    }
  }
  return { isOpen: false, closesAt: null, reopenDay: null, reopenTime: null };
}

export function useOpenStatus() {
  const [status, setStatus] = useState(() => getOpenStatus());
  useEffect(() => {
    const t = setInterval(() => setStatus(getOpenStatus()), 30000);
    return () => clearInterval(t);
  }, []);
  return status;
}
